// src/pages/AllRequests.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { faFilter, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchBox from 'components/common/SearchBox';
import CompanyFilter from 'components/common/CompanyFilter';
import {
  fetchAllRequests,
  selectAllRequests,
  selectAllRequestsLoading,
  selectAllRequestsError
} from 'store/allRequestsSlice'; // Adjust path if necessary
import { fetchCompanies } from 'store/companySlice';
import { RootState, AppDispatch } from 'store';
import AllRequestsTable from 'components/devices/AllRequestsTable';
import PhoenixLoader from 'components/common/PhoenixLoader';

const AllRequestsPage = () => {
  const dispatch = useDispatch<AppDispatch>();

  const requests = useSelector(selectAllRequests);
  const loading = useSelector(selectAllRequestsLoading);
  const error = useSelector(selectAllRequestsError);
  const companies = useSelector((state: RootState) => state.companies.data);
  const auth = useSelector((state: RootState) => state.auth);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const isSuperAdmin = auth.role === 'super_user';

  useEffect(() => {
    dispatch(fetchAllRequests());
  }, [dispatch]);

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchCompanies());
    }
  }, [isSuperAdmin, dispatch]);

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
  };

  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      // Filter by company if a company is selected and user is super admin
      if (isSuperAdmin && selectedCompanyId !== null) {
        if (request.company_id !== selectedCompanyId) return false;
      }

      // Filter by priority
      if (
        selectedPriority &&
        request.priority?.toLowerCase() !== selectedPriority.toLowerCase()
      ) {
        return false;
      }

      // Filter by status
      if (
        selectedStatus &&
        request.status?.toLowerCase() !== selectedStatus.toLowerCase()
      ) {
        return false;
      }

      return true;
    });
  }, [
    requests,
    isSuperAdmin,
    selectedCompanyId,
    selectedPriority,
    selectedStatus
  ]);

  return (
    <div>
      <div className="mb-6">
        <h2
          className="mb-0"
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            letterSpacing: '-0.025em'
          }}
        >
          <FontAwesomeIcon
            icon={faClipboardList}
            className="me-2 text-primary"
          />
          All Requests
        </h2>
        <Row className="g-3 justify-content-end mb-0">
          <Col xs="auto">
            <div className="d-flex gap-2">
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  fontWeight: '400'
                }}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  style={{ fontSize: '0.875rem' }}
                />
                <span>Filter</span>
              </button>
              <SearchBox
                placeholder="Search requests..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </Col>
        </Row>

        {/* Filter Panel */}
        {showFilterPanel && (
          <div className="mb-3 mt-3">
            <div className="d-flex gap-2 flex-wrap">
              {isSuperAdmin && companies.length > 0 && (
                <CompanyFilter
                  companies={companies}
                  selectedCompanyId={selectedCompanyId}
                  onCompanySelect={handleCompanySelect}
                />
              )}
              <select
                className="form-select"
                value={selectedPriority || ''}
                onChange={e => setSelectedPriority(e.target.value || null)}
                style={{
                  width: 'auto',
                  minWidth: '150px',
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem'
                }}
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <select
                className="form-select"
                value={selectedStatus || ''}
                onChange={e => setSelectedStatus(e.target.value || null)}
                style={{
                  width: 'auto',
                  minWidth: '150px',
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem'
                }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="on-hold">On Hold</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  setSelectedCompanyId(null);
                  setSelectedPriority(null);
                  setSelectedStatus(null);
                }}
                style={{
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem'
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        <div className="mt-3">
          {loading ? (
            <div className="d-flex flex-column justify-content-center align-items-center min-vh-50">
              <PhoenixLoader />
            </div>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : (
            <AllRequestsTable
              requests={filteredRequests}
              searchQuery={searchQuery}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AllRequestsPage;
